<template>
<div class="py-6 px-4 prose prose-sm prose-blue sm:px-6">

  <h1 ref="title" class="text-2xl font-semibold mb-0">
    Keeping Children Safe in Education
  </h1>
  <p class="lead mt-3">
    Prove you are familiar with the latest guidance surrounding safeguarding.
  </p>


  <div v-if="loading" class="space-y-2">
    <div 
      v-for="i of 4" :key="i"
      class="w-full bg-gray-100 animate-pulse rounded-lg"
      :class="[`h-` + Math.floor((Math.random() * 10) + 5)]"
    ></div>
  </div>
  <div v-else-if="page === 'intro'" id="intro">

    <div v-if="signedLatest" class="p-4 bg-blue-50 rounded">
      <h3 class="mt-0">
        Thank you!
      </h3>
      <p class="mb-0">
        You have confirmed that you have read and understood the most recent version of KCSIE.
        No further action is required at this time.
      </p>
      <a
        :href="kcsieUrl"
        target="_blank"
        class="mt-3 flex items-center font-semibold space-x-2"
      >
        <span>KCSIE Guidelines - {{ dayjs(latestVersion).format('MMMM YYYY') }}</span>
        <ExternalLinkIcon class="w-4 h-4" />
      </a>
    </div>
    <div v-else-if="signedOld" class="p-4 bg-red-100 rounded">
      <p class="mt-0">
        You last confirmed that you had read and understood KCSIE <strong>{{ dayjs(lastSigned).format('MMMM YYYY') }}</strong>.
        However, a newer version is now available.
      </p>
      <p>
        Please familiarise yourself with this new version and complete our short questionnaire to verify your understanding.
      </p>
      <TailwindButton @click="showQuiz">
        Next
      </TailwindButton>
    </div>


    <h3>
      What is Keeping Children Safe in Education?
    </h3>
    <p>
      Keeping Children Safe in Education (KCSIE) is statutory guidance provided by the Department for Education to ensure those working in education understand their duties and responsibilities regarding the safety and welfare of children (defined as those under the age of 18).
      The guidelines undergo continuous review, usually resulting in an annual update.
    </p>


    <h3>
      Why is KCSIE important?
    </h3>
    <p>
      Safeguarding and promoting the welfare of children is the responsibility of <strong>everyone</strong>.
      Everyone who comes into contact with children and their families has a role to play.
      In order to fulfil this responsibility effectively, all practitioners should make sure their approach is child-centred.
      This means that they should consider, at all times, what is in the best interests of the child.
    </p>


    <h3>
      KCSIE and The Tutor Index
    </h3>
    <p>
      <strong>We strongly recommended that all tutors keep up to date with the current guidance.</strong>
    </p>
    <p>
      After you have read through the latest KCSIE guidance, we will then ask you a few questions to verify your understanding.
      You may need to repeat this exercise as updated versions of KCSIE are released.
    </p>
    <p>
      Tutors who have completed our safeguarding assessments and checks are likely to be promoted higher in our search results.
    </p>
    <TailwindButton
      v-if="!signedLatest"
      @click="showQuiz"
      size="large"
    >
      Get Started
      <ChevronRightIcon class="ml-1 -mr-1 w-6 h-6" />
    </TailwindButton>


    <h3>
      What should I do if I have concerns?
    </h3>
    <p class="font-semibold text-red-600">
      If you are concerned that a child may be in immediate danger, <span class="font-extrabold">call 999</span>.
    </p>
    <p>
      If you are worried about a child but they are not in any immediate danger you should share your concerns. You could:
    </p>
    <ul>
      <li>
        <strong>Contact the NSPCC Helpline</strong> on <a href="tel:08088005000">0808 800 5000</a> or by emailing <a href="mailto:help@nspcc.org.uk">help@nspcc.org.uk</a>.
        Their trained professionals will talk through your concerns with you and give you expert advice.
      </li>
      <li>
        <a href="https://www.gov.uk/report-child-abuse-to-local-council" class="font-semibold">Contact the child’s local authority.</a>
        Where a child is suffering, or is likely to suffer from harm, it is important that a referral to local authority children’s social care (and if appropriate the police) is made immediately.
        All you need is the child’s postcode.
      </li>
      <li>
        <strong>Contact the child’s school.</strong>
        It’s good practice to inform the child’s school of any child protection concerns you might have.
        They may already be working to support the child and their family.
        Or if they have also noticed concerns, your information might help them take appropriate action.
      </li>
      <li>
        <strong>Contact the Police.</strong>
      </li>
    </ul>
    <p>
      <strong>Only share your concerns with the child’s parents if doing so does not put the child at further risk.</strong>
    </p>

  </div>
  <div v-else-if="page === 'quiz'" id="quiz">

    <button type="button" @click="page = 'intro'" class="flex items-center space-x-0.5 text-gray-600">
      <ChevronLeftIcon class="w-5 h-5 text-gray-500" />
      <span>Back</span>
    </button>

    <h3>
      Step 1: Read the KCSIE guidelines
    </h3>
    <p>
      Take some time to read through the KCSIE guidelines.
    </p>
    <a
      :href="kcsieUrl"
      target="_blank"
      class="flex items-center p-3 bg-gray-50 border border-gray-300 rounded text-lg font-semibold space-x-2"
    >
      <span>KCSIE Guidelines - {{ dayjs(latestVersion).format('MMMM YYYY') }}</span>
      <ExternalLinkIcon class="w-5 h-5" />
    </a>


    <h3 class="mt-12">
      Step 2: Answer the following questions
    </h3>

    <form
      @submit.prevent="onSubmit"
      class="space-y-8"
    >

      <div>
        <label for="readKcsie" class="block text-sm font-semibold text-gray-700">
          1. Have you read and understood the latest KCSIE guidelines?
        </label>
        <TailwindButtonGroup
          :value="form.readKcsie"
          @click="form.readKcsie = $event"
          :options="[{ label: 'Yes', value: true }, { label: 'No', value: false }]"
          class="mt-2"
        />
        <p v-if="$v.form.readKcsie.$dirty && (!$v.form.readKcsie.required || !$v.form.readKcsie.checked)" class="mt-2 text-sm text-red-600">
          Please confirm you have read and understoof the KCSIE guidelines.
        </p>
      </div>

      <div
        v-for="(question, qIdx) of questions"
        :key="question.question"
        class="-m-4 sm:rounded md:m-0"
        :class="[qIdx % 2 ? 'px-4' : 'p-4 bg-gray-100']"
      >
        <label class="block text-sm font-semibold text-gray-700">
          {{ qIdx+2 }}. {{ question.question }}
        </label>
        <div
          v-for="(answer, aIdx) of question.answers"
          :key="answer.label"
          class="flex mt-1.5 space-x-2"
        >
          <input
            type="radio"
            v-model="form.answers[qIdx]"
            :id="`${qIdx}-${aIdx}`"
            :value="answer"
            @change="$v.form.answers.$each[qIdx].$reset()"
            class="p-2 self-start"
            style="margin-top: 0.1875rem"
          />
          <label :for="`${qIdx}-${aIdx}`">
            {{ answer.label }}
          </label>
        </div>
        <p
          v-if="$v.form.answers.$each[qIdx].$dirty && !$v.form.answers.$each[qIdx].required"
          class="mt-2 mb-0 text-sm text-red-600"
        >
          Please answer this question.
        </p>
        <p
          v-if="$v.form.answers.$each[qIdx].$dirty && !$v.form.answers.$each[qIdx].correct"
          class="mt-2 mb-0 text-sm text-red-600"
        >
          This answer is incorrect.
        </p>
      </div>


      <TailwindButton
        type="submit"
        :loading="isSubmitting"
        :disabled="isSubmitting"
      >
        Submit  
      </TailwindButton>


      <!-- See comment in onSubmit explaining why we only check $dirty s-->
      <p v-if="$v.form.readKcsie.$dirty" class="text-sm text-red-600">
        There are some validation errors.
        Please review them and try again.
      </p>

    </form>

  </div>
  <div v-else>

    Signed!

  </div>

</div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { ExternalLinkIcon } from '@vue-hero-icons/outline'
import { ChevronLeftIcon, ChevronRightIcon } from '@vue-hero-icons/solid'
import dayjs from 'dayjs'
import TailwindButtonGroup from './TailwindButtonGroup.vue';
import questions from './kcsie-questions.js'
import TailwindButton from './TailwindButton.vue';

const latestKcsieDocumentDate = new Date(2022, 8, 1)
const latestKcsieDocumentUrl = 'https://assets.publishing.service.gov.uk/government/uploads/system/uploads/attachment_data/file/1101457/KCSIE_2022_Part_One.pdf'

export default {
  components: {
    ExternalLinkIcon,
    TailwindButtonGroup,
    TailwindButton,
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  data() {
    return {
      loading: false,
      page: 'intro',
      latestVersion: latestKcsieDocumentDate,
      kcsieUrl: latestKcsieDocumentUrl,
      showWhy: false,
      questions,
      form: {
        readKcsie: null,
        answers: [],
      },
      isSubmitting: false,
      dayjs,
    };
  },
  computed: {
    lastSigned: function() { return this.$store.getters.user.LatestKcsieCompletedWhen ? new Date(this.$store.getters.user.LatestKcsieCompletedWhen) : null },
    signedLatest: function() { return this.lastSigned && this.lastSigned >= this.latestVersion },
    signedOld: function() { return this.lastSigned && this.lastSigned < this.latestVersion },
  },
  methods: {
    showQuiz() {
      this.page = 'quiz'
      this.$nextTick(() => document.querySelector('#quiz').scrollIntoView({ block: 'start', behavior: 'smooth', }))
    },
    async onSubmit() {
      this.$v.$touch()
      if (this.$v.$invalid) return

      // This is necessary because the validation summary
      // only looks for whether or not the form is $dirty,
      // not whether it is both £dirty AND $invalid. The
      // reason for this is the installed version of vuelidate
      // does not allow $autoDirty to be disabled, which 
      // means once shown ($dirty) the validation summary
      // will show/hide automatically as the user changes
      // their answers.
      this.$v.$reset()

      this.isSubmitting = true

      await this.$store.dispatch('updateUser', { LatestKcsieCompletedWhen: new Date() })
      
      this.isSubmitting = false
      this.page = 'intro'
      this.$nextTick(() => document.querySelector('#intro').scrollIntoView({ block: 'start', behavior: 'smooth', }))

    },
  },
  validations: {
    form: {
      readKcsie: {
        required,
        checked: x => x === true,
      },
      answers: {
        $each: {
          required,
          correct: (foo) => {
            if (!foo) return true
            return foo.isAnswer
          }
        }
      }
    }
  },
  mounted() {
    this.form.answers = this.questions.map(x => null)
  },
}
</script>
